// @flow
import 'isomorphic-fetch';
import React, { useState, useEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import Slot from 'shared/editor/Slot';
import PostCard from 'shared/components/PostCard';
import type { $Slot } from 'shared/templates/types';
import withThemeProvider from 'shared/hoc/withThemeProvider';
import type { Styles, Region } from 'shared/types';
import AnimatedTitle from './AnimatedTitle';
import ArticlePagination from './ArticlePagination';
import YearFilter from './YearFilter';
import usePages from './usePages';

type TemplateSettings = {
  region: Region,
  title: string,
  all_years_tag: string,
  article_limit: string
}

type Props = {
  classes: Object,
  slots: $Slot[],
  templateSettings: TemplateSettings,
  widgetPresets: Array,
  path: string
}

const PressRoomLanding = (props: Props) => {
  const {
    slots, templateSettings, widgetPresets, path
  } = props;
  const c: Styles<typeof styles> = useStyles(props);

  const [scrollUp, setScrollUp] = useState(false);
  const [paginationState, setPaginationState] = useState({
    activePage: 0,
    activeYear: -1
  });

  const {
    activePage,
    activeYear
  } = paginationState;

  const container = useRef();

  const limit = Number(templateSettings.article_limit || 12);

  // Fetch pages when selection changes
  const { isFetching, posts, totalCount, years } = usePages(props.templateSettings.region, activePage, activeYear, limit);

  // Scroll up when going to next page
  useEffect(() => {
    if (container.current && scrollUp) {
      container.current.scrollIntoView({ behavior: 'smooth' });
      setScrollUp(false);
    }
  }, [scrollUp]);

  const getMorePosts = (start, year) => {
    setScrollUp(true);
    setPaginationState({
      activePage: start,
      activeYear: year
    });
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <div>
      <div className={c.menu}>
        <Slot id="menu-bar" slots={slots} widgetPresets={widgetPresets} path={path} />
      </div>
      <div>
        <div className={classNames(c.centeredContent, c.titleContainer)}>
          <AnimatedTitle>{templateSettings.title}</AnimatedTitle>
        </div>
      </div>
      <div className={c.topSection}>
        <Slot id="top-section" slots={slots} widgetPresets={widgetPresets} path={path} />
      </div>
      <div className={c.main} ref={container}>
        <div className={c.centeredContent}>
          <div className={c.leftCol}>
            <YearFilter
              allYearsLabel={templateSettings.all_years_tag}
              activeYear={activeYear}
              years={years}
              getMorePosts={getMorePosts}
            />
            {isFetching ? (
              <div className={c.loadingAnimation} />
            ) : (
              <div className={c.posts}>
                {posts.length > 0 && posts.map((post, j) => (
                  <PostCard
                    key={`${activePage}-${j}`}
                    post={post.template.props}
                    pressRoom
                  />
                ))}
                <ArticlePagination
                  activePage={activePage}
                  activeYear={activeYear}
                  pageCount={totalPages}
                  getMorePosts={getMorePosts}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Slot id="additional-content" slots={slots} widgetPresets={widgetPresets} path={path} />
      <footer id="footer" className={c.footer}>
        <Slot id="footer" slots={slots} widgetPresets={widgetPresets} path={path} />
      </footer>
    </div>
  );
}

const styles = {
  menu: {
    width: '100%',
    position: 'relative',
    zIndex: '10'
  },
  footer: {
    width: '100%'
  },
  centeredContent: {
    width: '100%',
    maxWidth: '1596px',
    margin: 'auto',
    display: 'flex',
    flexWrap: 'wrap',
    paddingLeft: '36px',
    paddingRight: '36px'
  },
  titleContainer: {
    marginTop: '100px',
    padding: '33px 0px 30px 36px',
    // Reduce padding on Press Room mobile
    '@media(max-width: 575px)': {
      marginTop: '88px',
      paddingBottom: '0px'
    }
  },
  main: {
    backgroundColor: '#f7f7f7',
    paddingTop: '36px'
  },
  leftCol: {
    width: '100%'
  },
  topSection: {
    width: '100%'
  },
  loadingAnimation: {
    fontSize: '0',
    width: '40px',
    height: '40px',
    margin: '72px auto',
    borderRadius: '40px',
    padding: '0',
    border: '3px solid #000',
    borderBottom: '3px solid rgba(0,0,0,0.0)',
    borderLeft: '3px solid rgba(0,0,0,0.0)',
    backgroundColor: 'transparent !important',
    animationName: '$rotateAnimation',
    animationDuration: '1s',
    animationDelay: '0.2s',
    animationIterationCount: 'infinite'
  },
  '@keyframes rotateAnimation': {
    from: {
      transform: 'rotate(0deg)'
    },
    to: {
      transform: 'rotate(360deg)'
    }
  },
  posts: {
    display: 'flex',
    flexWrap: 'wrap'
  }
};

const useStyles = createUseStyles(styles);

export default withThemeProvider(PressRoomLanding);
